import React, { useState } from 'react';
import { NavigateFunction } from 'react-router-dom';
import Translate from '../../Translate/Translate';
import Alert, { Type } from '../../../components/Alert';
import Input from '../Input/Input';
import useStores from '../../../hooks/useStores';
import Checkbox from '../Input/Checkbox';

interface Props {
  navigate: NavigateFunction;
  toggle: () => void;
}

interface RegisterErrors {
  email?: string[];
  password?: string[];
}

const mapEmailErrorToKeyword = (error: string) => {
  if (error === 'The email has already been taken.') return 'email-used';
  if (error === 'The email must be a valid email address.') return 'authemail';
  return '';
};

const mapPasswordErrorToKeyword = (error: string) => {
  if (error === 'The password must be at least 6 characters.')
    return 'passwordlength';
  if (error === 'The password confirmation does not match.')
    return 'passwordmatch';
  return '';
};

const Register = ({ navigate, toggle }: Props) => {
  const { authentication, user, cart } = useStores();
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerPasswordConfirmation, setRegisterPasswordConfirmation] =
    useState('');
  const [registerNewsletter, setRegisterNewsletter] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [message, setMessage] = useState('');
  const [emailErrors, setEmailErrors] = useState<string[]>([]);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [errorsArea, setErrorsArea] = useState(false);

  const resetErrors = () => {
    setEmailErrors([]);
    setPasswordErrors([]);
    setErrorsArea(false);
  };

  const tryToRegister = async () => {
    if (!registerEmail) {
      setMessage('email');
      resetErrors();
      return;
    }

    if (!registerPassword && !registerPasswordConfirmation) {
      setMessage('password');
      resetErrors();
      return;
    }

    if (!registerEmail.includes('@') || !registerEmail.includes('.')) {
      setMessage('authemail');
      return;
    }

    if (!termsAccepted) {
      setMessage('terms');
      resetErrors();
      return;
    }

    setMessage('');
    resetErrors();

    authentication.registerEmail = registerEmail;
    authentication.registerPassword = registerPassword;
    authentication.registerPasswordConfirmation = registerPasswordConfirmation;
    authentication.registerNewsletter = registerNewsletter;
    try {
      await authentication.register();
      await cart.create();
      await user.loadInfo();

      authentication.registerEmail = '';
      authentication.registerPassword = '';
      authentication.registerPasswordConfirmation = '';
      authentication.registerNewsletter = false;

      setRegisterEmail('');
      setRegisterPassword('');
      setRegisterPasswordConfirmation('');
      setRegisterNewsletter(false);
      setTermsAccepted(false);

      navigate('/toolaud');
      toggle();
    } catch (error) {
      const errors = (error || {}) as RegisterErrors;
      setEmailErrors(errors.email || []);
      setPasswordErrors(errors.password || []);
      setErrorsArea(true);
    }
  };

  const showErrorBox = errorsArea || !!message;

  return (
    <div className="py-10">
      <h3 className="text-3xl leading-10 font-axi-bold">
        <Translate component="login" keyword="register-info" />
      </h3>

      <p className="flex text-sm flex-[0_0_100%] leading-7 mt-4 mb-5 mx-0">
        <Translate component="login" keyword="register-offer" />
      </p>

      <div>
        <div>
          <Input
            className="w-full flex-[0_0_100%] m-0 pt-0 pb-5 px-0"
            type="text"
            name="registerEmail"
            id="registerEmail"
            keyword="email"
            value={registerEmail}
            onChange={(e) => setRegisterEmail(e.target.value)}
          />
          <div className="flex flex-wrap justify-between">
            <Input
              className="m-0 pb-5 lg:pb-2.5 lg:pr-2.5 px-0"
              name="registerPassword"
              id="registerPassword"
              type="password"
              keyword="password"
              value={registerPassword}
              onChange={(e) => setRegisterPassword(e.target.value)}
            />

            <Input
              className="m-0 pb-5 lg:pb-2.5 lg:pr-2.5 px-0"
              type="password"
              keyword="password-again"
              name="registerPasswordConfirmation"
              id="registerPasswordConfirmation"
              value={registerPasswordConfirmation}
              onChange={(e) => setRegisterPasswordConfirmation(e.target.value)}
            />
          </div>
        </div>

        <Alert show={showErrorBox} type={Type.ERROR}>
          {emailErrors.map((error, index) => (
            <p key={index}>
              <Translate
                component="login-error"
                keyword={mapEmailErrorToKeyword(error)}
              />
            </p>
          ))}

          {passwordErrors.map((error, index) => (
            <p key={index}>
              <Translate
                component="login-error"
                keyword={mapPasswordErrorToKeyword(error)}
              />
            </p>
          ))}

          {message ? (
            <Translate component="login-error" keyword={message} />
          ) : null}
        </Alert>
        <div className="my-5">
          <Checkbox
            className="h-8"
            id="terms"
            label="Nõustun"
            linkText="kasutustingimustega*"
            linkHref="/tingimused"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            required
            iconSize="size-4"
            checkboxSize="size-6"
            textSize="text-sm/8"
          />
          <Checkbox
            className="h-8"
            id="newsletter"
            label="Liitun uudiskirjaga"
            checked={registerNewsletter}
            onChange={(e) => setRegisterNewsletter(e.target.checked)}
            iconSize="size-4"
            checkboxSize="size-6"
            textSize="text-sm/8"
          />
        </div>

        <button
          onClick={tryToRegister}
          className="block cursor-pointer rounded border text-sm mt-5 px-5 py-3.5 border-solid border-rgrey-light hover:border hover:text-white hover:border-solid hover:bg-rgreen bg-white font-axi-bold"
        >
          <Translate component="login" keyword="register-button" />
        </button>
      </div>
    </div>
  );
};

export default Register;
